"use client";

import { useEffect, useRef } from "react";
import { apiFetch } from "@/lib/api";
import { browserTimeZone } from "@/lib/deadline";
import type { UserPreferences } from "@/lib/types";
import { rememberAccountTimeZone } from "@/hooks/useAccountTimeZone";

/**
 * Store the browser's time zone for a signed-in user who has none yet.
 *
 * Deadlines in emails and a picked "sign by" date are read in the SENDER's zone
 * (backend `deadlines.py`); with none stored, both fall back to UTC. Until
 * 2026-10-08 the zone was only detected when the Settings page was opened, so
 * most senders had none. Never overrides a stored choice, and never runs while
 * impersonating — that would write the admin's zone onto the customer.
 */
export function usePersistTimeZone(opts: {
  isAuthenticated: boolean;
  isImpersonating: boolean;
  getAccessToken: () => string | null;
}) {
  const { isAuthenticated, isImpersonating, getAccessToken } = opts;
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !isAuthenticated || isImpersonating) return;
    const token = getAccessToken();
    const detected = browserTimeZone();
    if (!token || !detected) return;
    done.current = true;
    (async () => {
      try {
        const prefs = await apiFetch<UserPreferences>("/auth/me/preferences", { token });
        if (prefs.timezone) { rememberAccountTimeZone(prefs.timezone); return; }
        const saved = await apiFetch<UserPreferences>("/auth/me/preferences", {
          method: "PUT",
          token,
          body: JSON.stringify({ timezone: detected }),
        });
        rememberAccountTimeZone(saved.timezone || detected);
      } catch {
        // Best effort: without a stored zone, deadlines read and print as UTC.
      }
    })();
  }, [isAuthenticated, isImpersonating, getAccessToken]);
}
